import { useCallback, useMemo, useRef, useState } from 'react';
import type React from 'react';
import { Button, Modal, message } from 'antd';
import { ExclamationCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { ProductState } from '@tcr-erp/shared';
import { PageShell } from '@/components/PageShell/PageShell';
import { ERPTable } from '@/components/DataTable/ERPTable';
import { useProductList } from '@/hooks/useProductList';
import { useDeleteProduct, useUpdateProductState, useBulkUpdateProductState, useBulkUpdateProductCategory } from '@/hooks/useProductMutations';
import { useCategoryOptions } from '@/hooks/useCategories';
import { useTableState } from '@/hooks/useTableState';
import { useRbac } from '@/hooks/useRbac';
import { useSerialTrackingEnabled } from '@/hooks/useGeneralConfig';
import { ROUTES } from '@/constants/routes';
import type { ProductListQuery } from '@/services/queryKeys';
import { buildProductColumns, PRODUCT_TABLE_KEY, type ProductListItem } from './parts/columns';
import { PRODUCT_STATE_LABEL } from './parts/constants';
import { DEFAULT_PRODUCT_FILTER, type ProductFilterValue } from './parts/filter-state';
import { ProductToolbar } from './parts/filters';
import { BulkToolbar } from './components/BulkToolbar';
import { BulkMoveCategoryModal, type BulkMoveCategoryValues } from './components/BulkMoveCategoryModal';
import { ExcelProductButton } from './components/ExportProductButton';

export function ProductsPage() {
  const navigate = useNavigate();
  const { hasAny } = useRbac();
  const { enabled: serialTrackingEnabled, isLoading: isSerialSettingLoading } =
    useSerialTrackingEnabled();
  const { page, pageSize, setTableState } = useTableState({ page: 1, pageSize: 20 });
  const [filter, setFilter] = useState<ProductFilterValue>(DEFAULT_PRODUCT_FILTER);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const openSettingsRef = useRef<(() => void) | undefined>(undefined);

  const deleteMutation = useDeleteProduct();
  const activateMutation = useUpdateProductState();
  const bulkStateMutation = useBulkUpdateProductState();
  const bulkCategoryMutation = useBulkUpdateProductCategory();
  const [moveCategoryOpen, setMoveCategoryOpen] = useState(false);

  const query: ProductListQuery = useMemo(() => {
    const trimmedQ = filter.q?.trim();
    return {
      ...filter,
      q: trimmedQ ? trimmedQ : undefined,
      page,
      page_size: pageSize,
    };
  }, [filter, page, pageSize]);

  const { data, isLoading } = useProductList(query);
  const { nameById: categoryMap, options: categoryOptions } = useCategoryOptions();
  const canUpdate = hasAny(['products.update']);
  const selectedProductIds = useMemo(
    () => selectedRowKeys.filter((k): k is string => typeof k === 'string'),
    [selectedRowKeys],
  );

  /** TCR-2484: chuyển danh mục hàng loạt; sản phẩm đã đúng danh mục không tính là "đã cập nhật". */
  const handleMoveCategory = async (values: BulkMoveCategoryValues) => {
    try {
      const result = await bulkCategoryMutation.mutateAsync({ ids: selectedProductIds, ...values });
      const unchanged = result.matched - result.updated;
      void message.success(
        `Đã cập nhật danh mục cho ${result.updated} sản phẩm${unchanged > 0 ? ` (${unchanged} sản phẩm đã đúng danh mục)` : ''}`,
      );
      setMoveCategoryOpen(false);
      setSelectedRowKeys([]);
    } catch {
      void message.error('Chuyển danh mục thất bại');
    }
  };


  // Search is applied server-side via the `q` query param; the rows returned are
  // already the current page of the full result set.
  const rows = data?.data ?? [];

  const handleClone = useCallback(
    async (record: ProductListItem) => {
      navigate(ROUTES.products.new, { state: { clonedProduct: record } });
    },
    [navigate],
  );

  const handleDelete = useCallback(
    (id: string, name: string) => {
      Modal.confirm({
      title: 'Ẩn sản phẩm',
      icon: <ExclamationCircleOutlined />,
      content: `Bạn muốn ẩn sản phẩm "${name}"? Sản phẩm sẽ không hiển thị trên kênh bán hàng.`,
      okText: 'Ẩn',
        okType: 'danger',
        cancelText: 'Huỷ',
        onOk: async () => {
          try {
            await deleteMutation.mutateAsync(id);
            void message.success('Đã ẩn sản phẩm');
          } catch {
            void message.error('Xoá thất bại');
          }
        },
      });
    },
    [deleteMutation],
  );

  // TCR-1840 — đối xứng với handleDelete, chuyển sản phẩm đang ẩn (INACTIVE) trở lại ACTIVE.
  const handleActivate = useCallback(
    (id: string, name: string) => {
      Modal.confirm({
        title: 'Hiện sản phẩm',
        icon: <ExclamationCircleOutlined />,
        content: `Bạn muốn hiện sản phẩm "${name}" trở lại? Sản phẩm sẽ hiển thị lại trên kênh bán hàng.`,
        okText: 'Hiện',
        cancelText: 'Huỷ',
        onOk: async () => {
          try {
            await activateMutation.mutateAsync({ id, state: ProductState.ACTIVE });
            void message.success('Đã hiện sản phẩm');
          } catch {
            void message.error('Cập nhật thất bại');
          }
        },
      });
    },
    [activateMutation],
  );

  const columns = useMemo(
    () =>
      buildProductColumns({
        categoryMap,
        onClone: handleClone,
        onDelete: handleDelete,
        onActivate: handleActivate,
        navigate,
        serialTrackingEnabled,
        // TCR-1837 — menu "..." (Sửa/Nhân bản/Đồng bộ MISA/Ẩn) phải ẩn đúng theo quyền thay vì
        // luôn hiện; "Xem trước" không cần gate vì user đã đứng được ở màn list (products.view).
        canUpdate: hasAny(['products.update']),
        canCreate: hasAny(['products.create']),
        canDelete: hasAny(['products.delete']),
      }),
    [categoryMap, handleClone, handleDelete, handleActivate, navigate, serialTrackingEnabled, hasAny],
  );

  const handleFilterChange = useCallback(
    (next: ProductFilterValue) => {
      setFilter(next);
      setTableState({ page: 1 });
    },
    [setTableState],
  );

  const handleResetFilter = useCallback(() => {
    setFilter(DEFAULT_PRODUCT_FILTER);
    setTableState({ page: 1 });
  }, [setTableState]);

  return (
    <PageShell
      fullHeight
      hideHeader
      title="Sản phẩm"
      filters={
        <ProductToolbar
          value={filter}
          onChange={handleFilterChange}
          onReset={handleResetFilter}
          onOpenSettings={() => openSettingsRef.current?.()}
          actions={
            <>
              <ExcelProductButton filter={filter} canImport={hasAny(['products.create'])} />
              {hasAny(['products.create']) && (
                <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate(ROUTES.products.new)}>
                  Thêm sản phẩm
                </Button>
              )}
            </>
          }
        />
      }
    >
      {selectedRowKeys.length > 0 && (
        <BulkToolbar
          count={selectedRowKeys.length}
          loading={bulkStateMutation.isPending}
          onStateChange={async (state) => {
            try {
              const ids = selectedRowKeys.filter((k): k is string => typeof k === 'string');
              await bulkStateMutation.mutateAsync({ ids, state });
              const stateLabel = PRODUCT_STATE_LABEL[state]?.toLowerCase() ?? 'cập nhật';
              void message.success(`Sản phẩm đã được ${stateLabel} thành công`);
              setSelectedRowKeys([]);
            } catch {
              void message.error('Cập nhật thất bại');
            }
          }}
          onClear={() => setSelectedRowKeys([])}
          onMoveCategory={canUpdate ? () => setMoveCategoryOpen(true) : undefined}
        />
      )}
      <BulkMoveCategoryModal
        open={moveCategoryOpen}
        count={selectedProductIds.length}
        loading={bulkCategoryMutation.isPending}
        categoryOptions={categoryOptions}
        onCancel={() => setMoveCategoryOpen(false)}
        onSubmit={handleMoveCategory}
      />
      <ERPTable
        tableKey={PRODUCT_TABLE_KEY}
        hideSettingsButton
        registerOpenSettings={(open) => {
          openSettingsRef.current = open;
        }}
        rowKey="_id"
        loading={isLoading || isSerialSettingLoading}
        dataSource={rows}
        columns={columns}
        fitHeight
        bottomOffset={12}
        rowSelection={{
          selectedRowKeys,
          onChange: setSelectedRowKeys,
          preserveSelectedRowKeys: true,
        }}
        pagination={{
          current: page,
          pageSize,
          total: data?.total ?? 0,
          showSizeChanger: true,
          showTotal: (t) => `${t} sản phẩm`,
          onChange: (p, ps) => setTableState({ page: p, pageSize: ps }),
        }}
      />
    </PageShell>
  );
}
